import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { HeartHandshake, Eye, Mail, Send, ChevronDown, ChevronUp } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AliancaCard = () => {
  const [data, setData] = useState(null);
  const [reminder, setReminder] = useState(null);
  const [testEmail, setTestEmail] = useState("");
  const [openHistory, setOpenHistory] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/alianca`, { withCredentials: true });
      setData(r.data);
      const r2 = await axios.get(`${API}/admin/capital-reminder`, { withCredentials: true });
      setReminder(r2.data);
    } catch (e) {
      console.error("[Alianca] load:", e?.message);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const preview = () => window.open(`${API}/admin/alianca/preview`, "_blank");

  const sendTest = async () => {
    if (!testEmail.trim()) { toast.error("Indique o email de teste."); return; }
    try {
      toast.info("A preparar a mensagem (Evangelho + reflexão)…");
      await axios.post(`${API}/admin/alianca/test`, { email: testEmail.trim() }, { withCredentials: true });
      toast.success(`Teste enviado para ${testEmail.trim()}.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro no envio de teste.");
    }
  };

  const sendNow = async () => {
    if (!window.confirm(`Enviar a mensagem de Renovação da Aliança a ${data?.recipients_count} sócio(s) agora?`)) return;
    try {
      await axios.post(`${API}/admin/alianca/send`, {}, { withCredentials: true });
      toast.success("Mensagem em envio.");
      setTimeout(load, 3000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro no envio.");
    }
  };

  const sendTestReminder = async () => {
    if (!testEmail.trim()) { toast.error("Indique o email de teste."); return; }
    try {
      await axios.post(`${API}/admin/capital-reminder/test`, { email: testEmail.trim() }, { withCredentials: true });
      toast.success(`Teste do lembrete enviado para ${testEmail.trim()}.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro no envio de teste.");
    }
  };

  if (!data) return null;
  const history = data.history || [];

  return (
    <div className="bg-white border border-[hsl(var(--border))] mb-6" data-testid="alianca-card">
      <div className="p-5 flex flex-wrap items-center gap-3">
        <HeartHandshake className="h-5 w-5 text-[#B08D1E] shrink-0" />
        <div className="flex-1 min-w-[220px]">
          <div className="font-semibold text-[hsl(var(--foreground))]">Mensagem do Dia 18 — Renovação da Aliança</div>
          <div className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">
            Automática a cada dia 18 às 17h00 · Evangelho do dia + reflexão · próximo envio: {data.next_date} · {data.recipients_count} destinatário(s)
          </div>
        </div>
        <button onClick={preview} title="Pré-visualizar (gera Evangelho + reflexão do próximo dia 18)"
          className="p-2 border border-[hsl(var(--border))] hover:border-[hsl(var(--accent))]"
          data-testid="alianca-preview-btn">
          <Eye className="h-4 w-4" />
        </button>
        <button onClick={sendNow} title="Enviar agora a todos os sócios"
          className="p-2 border border-[hsl(var(--primary))] text-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))] hover:text-white"
          data-testid="alianca-send-now-btn">
          <Send className="h-4 w-4" />
        </button>
      </div>
      <div className="px-5 pb-4 flex flex-wrap items-center gap-2 text-xs text-[hsl(var(--muted-foreground))]">
        <Mail className="h-3.5 w-3.5" />
        Teste para:
        <input value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="email@exemplo.pt"
          className="border border-[hsl(var(--border))] px-2 py-1 text-xs w-56" data-testid="alianca-test-email" />
        <button onClick={sendTest}
          className="px-3 py-1 border border-[hsl(var(--border))] hover:border-[hsl(var(--accent))]"
          data-testid="alianca-send-test-btn">
          Enviar teste
        </button>
      </div>

      {reminder && (
        <div className="border-t border-[hsl(var(--border))] px-5 py-4 flex flex-wrap items-center gap-3"
          data-testid="capital-reminder-row">
          <div className="flex-1 min-w-[220px]">
            <div className="text-sm font-medium text-[hsl(var(--foreground))]">Lembrete do Dia 16 — imprimir o Capital de Graças</div>
            <div className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">
              Automático a cada dia 16 às 17h00 · próximo: {reminder.next_date} (queima a {reminder.burn_label}) · {reminder.recipients_count} destinatário(s)
              {reminder.history?.[0] ? ` · último: ${reminder.history[0].date} enviado a ${reminder.history[0].sent_count}` : ""}
            </div>
          </div>
          <button onClick={() => window.open(`${API}/admin/capital-reminder/preview`, "_blank")}
            title="Pré-visualizar lembrete"
            className="p-2 border border-[hsl(var(--border))] hover:border-[hsl(var(--accent))]"
            data-testid="capital-reminder-preview-btn">
            <Eye className="h-4 w-4" />
          </button>
          <button onClick={sendTestReminder} title="Enviar teste do lembrete para o email acima"
            className="p-2 border border-[hsl(var(--border))] hover:border-[hsl(var(--accent))]"
            data-testid="capital-reminder-test-btn">
            <Mail className="h-4 w-4" />
          </button>
        </div>
      )}

      {history.length > 0 && (
        <div className="border-t border-[hsl(var(--border))] px-5 py-4">
          <div className="text-[11px] uppercase tracking-wider text-[hsl(var(--muted-foreground))] mb-2">Histórico</div>
          <ul className="space-y-2 text-sm">
            {history.map((h) => (
              <li key={h.date} className="border-b border-[hsl(var(--border))]/60 pb-2" data-testid={`alianca-history-${h.date}`}>
                <div className="flex items-center gap-3">
                  <span className="font-medium text-[hsl(var(--primary))]">{h.month_label}</span>
                  <span className="text-xs text-[hsl(var(--muted-foreground))]">
                    {h.gospel_reference || "sem Evangelho"} ·{" "}
                    {h.status === "sent" ? `enviada a ${h.sent_count} (${h.trigger === "cron" ? "automático" : "manual"})` : "preparada, por enviar"}
                  </span>
                  <button onClick={() => setOpenHistory(openHistory === h.date ? null : h.date)}
                    className="ml-auto p-1 text-[hsl(var(--muted-foreground))]" data-testid={`alianca-history-toggle-${h.date}`}>
                    {openHistory === h.date ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </button>
                </div>
                {openHistory === h.date && (
                  <p className="mt-2 text-xs text-[hsl(var(--foreground))] leading-relaxed whitespace-pre-wrap bg-[#FBF6E8] border border-[#E3D5A8] p-3">
                    {h.reflection}
                  </p>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default AliancaCard;
